import { useState } from 'react';

import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ChuteScheduleRow } from '@/rtk-query.gql';
import { useAccessToken } from '@/services/auth';
import { updateAspirationTimes as updateAspirationTimesRest } from '@/services/chute-scheduler/api';

type Props = { className?: string };

export const SaveButton = ({ className }: Props) => {
  const dispatch = useDispatch();
  const token = useAccessToken();
  const [params] = useSearchParams();
  const isMultiSiteEnabled = useFeatureFlag('Chute_Scheduler_Multi_Site');
  const hasSite = isMultiSiteEnabled
    ? params.getAll('site').length > 0
    : !!params.get('site');
  const date = params.get('date');
  const [showConfirm, setShowConfirm] = useState(false);

  const { isSorted, schedulingRows, selectedSchedulingRows } = useAppSelector(
    (s) => s.chuteScheduler,
  );

  const [restLoading, setRestLoading] = useState(false);
  const isLoading = restLoading;

  const saveTimes = async () => {
    setShowConfirm(false);
    const payload = schedulingRows.map((row: ChuteScheduleRow) => ({
      id: row.aspirationId,
      time: row.aspTime,
    }));

    setRestLoading(true);
    try {
      await updateAspirationTimesRest(token, payload);
    } finally {
      setRestLoading(false);
    }
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
      rows: [],
    });
  };

  const disable =
    !date ||
    !hasSite ||
    selectedSchedulingRows.length !== schedulingRows.length ||
    isSorted;

  return (
    <>
      <Button
        className={`gap-1.5 ${className ?? ''}`.trim()}
        onClick={() => setShowConfirm(true)}
        disabled={disable || isLoading}
      >
        {isLoading ? (
          <i className="pi pi-spin pi-spinner text-xs" />
        ) : (
          <i className="pi pi-save text-xs" />
        )}
        {t('common.save')}
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save schedule?</DialogTitle>
            <DialogDescription>
              Once you save, you will not be able to revert without manually
              re-applying the original times. Do you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={saveTimes}>Yes, Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
